import { Info, AlertCircle } from "lucide-react";

/**
 * Course Offer Status — surfaces the "we don't have per-course data yet"
 * signal loudly so it never gets hidden by the parent uni's typical range.
 *
 * Displayed on the Cluster 2 (Applying) section when the degree lacks
 * per-course A-level data AND the parent uni has an approved
 * `entry_requirements_typical` range on record.
 *
 * Design principle
 * ----------------
 * Uni-wide ranges (Imperial "AAA to A*A*A", UCL "A*A*A to ABB") are
 * bimodal — Physics demands A*A*A + STEP, History demands ABB.
 * Cascading them onto every course would be quietly wrong, so the
 * range lives only on the university and is shown here as CONTEXT
 * next to a clear "not yet sourced" label. That way:
 *   1. Users see helpful uni-wide context.
 *   2. Users never mistake it for the specific course's offer.
 *   3. Ingestion pipelines still see the field as empty and keep
 *      trying to source real per-course data.
 */
export default function CourseOfferStatus({ degree, uni }) {
  const uniTypical = uni?.entry_requirements_typical || null;
  if (!uniTypical) return null;

  const courseHasOffer = !!(
    degree?.entry_requirements?.a_level?.typical ||
    degree?.entry_requirements?.ucas_tariff?.standard ||
    degree?.hesa_stats?.typical_offer_text ||
    degree?.hesa_stats?.typical_a_level
  );
  if (courseHasOffer) return null;

  const range = uniTypical.a_level_range || uniTypical.published_ucas_tariff
    ? (uniTypical.a_level_range
        ? uniTypical.a_level_range
        : `${uniTypical.published_ucas_tariff} UCAS tariff points`)
    : null;

  return (
    <div
      data-testid="course-offer-status"
      className="mb-5 rounded-lg border border-amber-300 bg-amber-50/60 p-4"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="w-4 h-4 text-amber-700 flex-none mt-0.5" />
        <div className="flex-1 min-w-0">
          <div
            data-testid="course-offer-status-headline"
            className="text-sm font-semibold text-amber-900"
          >
            Course offer: not yet sourced for this specific course
          </div>
          <div className="text-[12px] text-amber-900/80 mt-0.5 leading-relaxed">
            We haven't yet extracted the offer for this exact degree from
            {" "}{uni?.name || "the university"}'s course page. The uni-wide
            typical range below is context only — always check the specific
            course page for the offer that applies to this degree.
          </div>
        </div>
      </div>

      {range && (
        <div
          data-testid="course-offer-status-uni-context"
          className="mt-3 ml-7 rounded-md border border-amber-200/70 bg-white/70 px-3 py-2 flex items-start gap-2"
        >
          <Info className="w-3.5 h-3.5 text-amber-600 flex-none mt-0.5" />
          <div className="text-[12px] text-stone-700 leading-relaxed">
            <span className="font-medium text-stone-900">
              {uni?.name || "This university"} typically requires{" "}
              <span className="font-mono">{range}</span>
            </span>
            {" "}across its undergraduate degrees
            {uniTypical.entry_gate && uniTypical.entry_gate !== "grades"
              ? ` (${uniTypical.entry_gate}-based selection where relevant)`
              : ""
            }. Individual courses may ask for higher or lower — check the
            course page linked below for the specific offer.
            {uniTypical.source_url && (
              <>
                {" "}
                <a
                  href={uniTypical.source_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="course-offer-status-source-link"
                  className="text-amber-800 hover:underline"
                >
                  View uni-wide entry-requirements page →
                </a>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
